/**
 * 用語（Term）の入力スキーマ — 区画 3 × 表現の型 10（NE §1-attrs の Term）。
 *
 * 今の用語は「語の定義」で、`kind` 4 種（object / classification / state / verb）と説明と
 * 言い換えだけを持つ。作り直しの用語は**欄に制約を掛ける材料**になる。判定表のセルに書ける
 * 値・式の単位の一致・ありえない状態遷移を、用語の側から機械で決めるためである。
 *
 * 用語を 1 つ登録するときは上から順に 3 つ決め、決めた答えで残りの欄が「必須」か「書けない」
 * かが機械的に決まる。
 *
 * 1. **区画**（`section`）— クラス / プロパティ / 値 のちょうど 1 つ
 * 2. プロパティなら **属するクラス**（`ownerClass`）と **表現の型**（`representation`）
 * 3. 表現の型が決まると、追加で要る欄（`values` / `unit` / `transitions`）が決まる
 *
 * この必須と「書けない」は形式の検査で止める（区画と表現の型で枝を選ぶ判別ユニオン。今の
 * `kind` で枝を選ぶ作りと同じ）。グラフ全体を見ないと分からないもの — 参照先の区画が合って
 * いるか（制約 47）、状態遷移の値が取りうる値に入っているか（制約 50）— はリリースゲートに
 * 回す。
 *
 * 動詞はここに居ない。用語から出て `Verb` になった（`masters.ts`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの用語（区画 3 × 表現の型 10）の zod 入力スキーマ。書込時の形式検査の単一ソース
 * @graph-connects none
 */
import { z } from 'zod';
import { localizedText, noteText, synonymSchema } from '../node-schemas.js';
import { optionalSpecElementId, specElementId } from './ids.js';
/**
 * 用語の区画 3 種（NE §1-attrs）。その語が何であるかを決める。
 *
 * - `class` —「〜が 3 件」と数えられるもの（選定対象 / オーダー / スタイリスト）
 * - `property` —「どのクラスのものか」が言える、そのクラスの全員が持つ性質（選定の状態）
 * - `value` —「どのプロパティの値か」が言える、プロパティが取る 1 つの値（選定中）
 *
 * 今の `kind` 4 種を置き換える。実データは object 86・verb 14 で、classification と state は
 * 0 件だった。object 86 件は人がクラス / プロパティ / 値へ振り分ける。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の区画（クラス / プロパティ / 値）の allowlist
 * @graph-connects none
 */
export const TERM_SECTIONS = ['class', 'property', 'value'];
/**
 * プロパティの表現の型 10 種（NE §1-attrs）。値をどう表すかを決める。
 *
 * 型が決まると、追加で要る欄が機械的に決まる。「値が変わるか」の印を人に付けさせる欄は
 * 要らない — `state` を選んだ瞬間に状態遷移が必須になるからである。
 *
 * | 型 | 取りうる値 | 単位 | 状態遷移 |
 * |---|---|---|---|
 * | `code` 区分 | 必須・2 つ以上 | 書けない | 書けない |
 * | `state` 状態 | 必須・2 つ以上 | 書けない | 必須・1 本以上 |
 * | `indicator` 真偽 | 必須・ちょうど 2 つ | 書けない | 書けない |
 * | `quantity` 個数 / `measure` 量 / `amount` 金額 / `dateTime` 日時 | 書けない | 必須 | 書けない |
 * | `numeric` 数 / `identifier` 識別子 / `text` 文 | 書けない | 書けない | 書けない |
 *
 * 区分と状態を分けるのは語の定義であって使われ方ではない。「選定の状態」は語そのものが変化を
 * 含み、「指名の区分」は分類であって変化しない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business プロパティの表現の型 10 種の allowlist
 * @graph-connects none
 */
export const TERM_REPRESENTATIONS = [
    'code',
    'state',
    'indicator',
    'quantity',
    'measure',
    'amount',
    'dateTime',
    'numeric',
    'identifier',
    'text',
];
/**
 * 並べ替えの属性に使える表現の型（順序を書ける型）。制約 47 が見る。
 *
 * 識別子と文だけが属性にできない。1 件を区別するための記号と、人が読む文には、先に回す
 * 順序を書く意味が無いためである。区分・状態・真偽は値どうしに大小が無いが、値の並び（どの
 * 値を先に回すか）で順序を書ける。個数・量・金額・日時・数は列挙（高い順 / 早い順）で書く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの属性に使える表現の型（識別子と文を除く 8 種）の allowlist
 * @graph-connects none
 */
export const ORDERED_TERM_REPRESENTATIONS = [
    'code',
    'state',
    'indicator',
    'quantity',
    'measure',
    'amount',
    'dateTime',
    'numeric',
];
/**
 * 状態遷移 1 本（今の値, 出来事, 次の値）。
 *
 * 今の値と次の値はそのプロパティの「取りうる値」に入っている値の用語、出来事は動詞
 * （`Verb`）の id である。処理の「変更」の「変える先」と突き合わせて、ありえない遷移を
 * 検出する（制約 50）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の状態遷移 1 本（今の値・出来事・次の値）の入力スキーマ
 * @graph-connects none
 */
export const termTransitionSchema = z.strictObject({
    from: specElementId(),
    event: specElementId(),
    to: specElementId(),
});
/**
 * 用語の区画に依らず共通の欄。
 *
 * `synonyms` に値が入るのは、同じ語を指す別表記が本文に現れたときだけである（NE §1-attrs が
 * 任意の欄として残した「説明の言い換え」1 欄）。`USES_TERM` は本文と用語の完全一致でしか
 * 張られないので、別表記を書いておかないとその本文から参照が生まれない。
 *
 * `note`（備考）は説明に収まらない補足（算出の手順・表・式）を書く欄で、**人が画面からだけ
 * 書く**。用語の書込の MCP の入口（`writeSpecTermElements`）は `note` を受けると拒否し、書き
 * 直すときは今の備考を引き継ぐ。画面の入口は `writeSpecTermNote` である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 用語の 4 区画すべてに共通する欄（id・ラベル・説明・言い換え・備考）の zod 形
 * @graph-connects none
 */
const termCommonShape = {
    id: optionalSpecElementId(),
    label: localizedText(),
    description: localizedText(),
    synonyms: z.array(synonymSchema).optional(),
    note: noteText().optional(),
};
/**
 * クラスの用語（選定対象 / オーダー / スタイリスト）。
 *
 * 属するクラス・表現の型・取りうる値・単位・状態遷移はどれも書けない。クラスは「数えられる
 * もの」そのもので、値の表し方を持たないためである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business クラスの用語の zod 入力スキーマ
 * @graph-connects none
 */
export const classTermInputSchema = z.strictObject({
    ...termCommonShape,
    section: z.literal('class'),
});
/**
 * 値の用語（選定中 / 個人指名 / 該当する）。
 *
 * どのプロパティの値かは書かない。プロパティの側の「取りうる値」から逆向きに導出する
 * （1 つの値の用語を複数のプロパティが指してよい）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 値の用語の zod 入力スキーマ
 * @graph-connects none
 */
export const valueTermInputSchema = z.strictObject({
    ...termCommonShape,
    section: z.literal('value'),
});
/**
 * プロパティの用語が区画によらず持つ欄（属するクラスまで）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business プロパティの用語に共通する欄（共通欄・区画・属するクラス）の zod 形
 * @graph-connects none
 */
const propertyCommonShape = {
    ...termCommonShape,
    section: z.literal('property'),
    ownerClass: specElementId(),
};
/**
 * 取りうる値を列挙する表現の型（区分・真偽）のプロパティ。
 *
 * `values` は区画が「値」の用語の id の並びで、**並びの順に意味がある**（並べ替えの
 * 「指定した並び」がこの順を使う）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 取りうる値を列挙するプロパティ（区分・真偽）の zod 入力スキーマ
 * @graph-connects none
 */
export const codeTermInputSchema = z.strictObject({
    ...propertyCommonShape,
    representation: z.literal('code'),
    values: z.array(specElementId()).min(2),
});
/**
 * 真偽のプロパティ（二重配送 → 該当する / 該当しない）。値は**ちょうど 2 つ**。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 真偽のプロパティの zod 入力スキーマ（取りうる値はちょうど 2 つ）
 * @graph-connects none
 */
export const indicatorTermInputSchema = z.strictObject({
    ...propertyCommonShape,
    representation: z.literal('indicator'),
    values: z.array(specElementId()).length(2),
});
/**
 * 状態のプロパティ（選定の状態 → 未選定 / 選定中 / 確定済）。
 *
 * 状態遷移が**必須**である。状態は「値から値への変わり方が決まっている」ものなので、遷移の
 * 無い状態は定義が済んでいない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 状態のプロパティの zod 入力スキーマ（取りうる値 2 つ以上と状態遷移 1 本以上）
 * @graph-connects none
 */
export const stateTermInputSchema = z.strictObject({
    ...propertyCommonShape,
    representation: z.literal('state'),
    values: z.array(specElementId()).min(2),
    transitions: z.array(termTransitionSchema).min(1),
});
/**
 * 単位が要る表現の型（個数・量・金額・日時）のプロパティ。
 *
 * 単位が無いと数の読み方が決まらない（`0.1` が率なのか件なのか分からない）ので必須にする
 * （制約 56）。
 *
 * @param representation 単位が要る表現の型のどれか
 * @returns その表現の型のプロパティの zod 入力スキーマ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 単位が要る表現の型のプロパティの zod 入力スキーマを組み立てる
 * @graph-connects none
 */
function measuredTermInputSchema(representation) {
    return z.strictObject({
        ...propertyCommonShape,
        representation: z.literal(representation),
        unit: specElementId(),
    });
}
/**
 * 追加の欄を持たない表現の型（数・識別子・文）のプロパティ。
 *
 * `text` は「検査できないことを型で明示するための行」である。書けないのではなく、機械が
 * 見ないことを型で宣言する。
 *
 * @param representation 追加の欄を持たない表現の型のどれか
 * @returns その表現の型のプロパティの zod 入力スキーマ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 追加の欄を持たない表現の型のプロパティの zod 入力スキーマを組み立てる
 * @graph-connects none
 */
function plainTermInputSchema(representation) {
    return z.strictObject({
        ...propertyCommonShape,
        representation: z.literal(representation),
    });
}
/**
 * プロパティの用語。表現の型で枝を選ぶ判別ユニオン 10 枝。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business プロパティの用語の zod 入力スキーマ（表現の型 10 種の判別ユニオン）
 * @graph-connects none
 */
export const propertyTermInputSchema = z.discriminatedUnion('representation', [
    codeTermInputSchema,
    stateTermInputSchema,
    indicatorTermInputSchema,
    measuredTermInputSchema('quantity'),
    measuredTermInputSchema('measure'),
    measuredTermInputSchema('amount'),
    measuredTermInputSchema('dateTime'),
    plainTermInputSchema('numeric'),
    plainTermInputSchema('identifier'),
    plainTermInputSchema('text'),
]);
/**
 * 用語の入力スキーマ。区画で枝を選ぶ判別ユニオン 3 枝。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの用語の zod 入力スキーマ（区画 3 種の判別ユニオン）
 * @graph-connects none
 */
export const termElementSchema = z.discriminatedUnion('section', [
    classTermInputSchema,
    valueTermInputSchema,
    propertyTermInputSchema,
]);
//# sourceMappingURL=term.js.map